<template>
  <button
    class="btn btn-warning btn-sm"
    data-testid="csv-export-btn"
    @click="handleCsvExport"
    :disabled="loading">
    <ProgressSpinner v-if="loading" style="width: 1rem; height: 1rem" strokeWidth="4" />
    <i v-else class="pi pi-file-excel"></i> 
    {{ text || 'CSV' }}
  </button>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ProgressSpinner from 'primevue/progressspinner';
import { useDateFormatter } from '../../composables/useDateFormatter';

const props = defineProps<{
  text?: string;
  config: Record<string, any>;
  data?: any[];
  getFilteredData?: () => any[];
}>();

const emit = defineEmits<{
  (e: 'csv-export'): void;
  (e: 'csv-error', error: any): void;
}>();

// Get i18n for formatting
const { n } = useI18n();
const { formatDate, formatDateTime } = useDateFormatter();

const loading = ref(false);

const handleCsvExport = async () => {
  if (loading.value) return;
  
  try {
    loading.value = true;
    
    await generateCsv();
    
    // Emit success event
    emit('csv-export');
    
  } catch (error) {
    console.error('Error generating CSV:', error);
    emit('csv-error', error);
  } finally {
    loading.value = false;
  }
};

const generateCsv = async () => {
  try {
    // Get filtered data or all data
    const filtered = props.getFilteredData?.() ?? [];
    const dataToExport = filtered.length > 0 ? filtered : (props.data ?? []);
    
    if (!dataToExport || dataToExport.length === 0) {
      throw new Error('No data to export');
    }
    
    // Get visible columns, flattening merged sub-columns into separate columns.
    // Tag merged sub-columns with _isMergedSub for the export helper.
    const columns = props.config.columns.flatMap((col: any) =>
      col.mergedColumns?.length
        ? [{ ...col, _isMergedSub: false }, ...col.mergedColumns.map((sc: any) => ({ ...sc, _isMergedSub: true }))]
        : [col]
    ).filter((col: any) => col.visible !== false);

    // Create CSV headers
    const headers = columns.map((column: any) => column.title || column.data);

    // Create CSV rows
    const rows = dataToExport.map((row: any) => {
      return columns.map((column: any) => {
        // Merged sub-columns hidden by visible() get empty value
        if (column._isMergedSub && column.visible && !column.visible(row)) {
          return '';
        }
        let cellValue = row[column.data];
        
        // Format cell value based on render type
        if (column.renderType) {
          cellValue = formatCellValueForCsv(cellValue, column.renderType);
        }
        
        // Convert to string and escape CSV values
        const cellText = String(cellValue || '');
        
        // Escape CSV values (handle quotes and commas)
        if (cellText.includes('"') || cellText.includes(',') || cellText.includes('\n')) {
          return `"${cellText.replace(/"/g, '""')}"`;
        }
        
        return cellText;
      });
    });
    
    // Combine headers and rows
    const csvContent = [headers, ...rows]
      .map(row => row.join(','))
      .join('\n');
    
    // Add BOM for UTF-8 support in Excel
    const csvWithBom = '\uFEFF' + csvContent;
    
    // Generate filename with .csv extension
    const baseFilename = props.config.filename || `relatorio_${new Date().toISOString().split('T')[0]}`;
    const filename = baseFilename.endsWith('.csv') ? baseFilename : `${baseFilename}.csv`;
    
    // Create and download file
    downloadCsv(csvWithBom, filename);
    
  } catch (error) {
    console.error('CSV Generation Error:', error);
    throw new Error(`Failed to generate CSV: ${error.message}`);
  }
};

const downloadCsv = (csvContent: any, filename: any) => {
  // Create blob with CSV content
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  
  // Create download link
  const link = document.createElement('a');
  
  // Create object URL for the blob
  const url = URL.createObjectURL(blob);
  
  // Set link properties
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  
  // Add to DOM, click, and remove
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up object URL
  URL.revokeObjectURL(url);
};

const formatCellValueForCsv = (value: any, renderType: any) => {
  if (value === null || value === undefined) return '';

  switch (renderType) {
    case 'currency':
      const currencyValue = parseFloat(value) || 0;
      return n(currencyValue, 'currency');
    case 'number':
      const numberValue = parseFloat(value) || 0;
      return n(numberValue, 'decimal');
    case 'percentage':
      const percentageValue = parseFloat(value) || 0;
      return n(percentageValue / 100, 'percent');
    case 'date':
      if (!value) return '';
      const date = new Date(value);
      return formatDate(date);
    case 'datetime':
      if (!value) return '';
      const datetime = new Date(value);
      return formatDateTime(datetime);
    case 'yes_no':
      return value ? 'Sim' : 'Não';
    case 'active_inactive':
      return value ? 'Ativo' : 'Inativo';
    default:
      return String(value);
  }
};
</script>
